import React from 'react';

import {
  FlagCircle as FlagCirgleIcon,
  PeopleAlt as PeopleAltIcon,
  Verified as VerifiedIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { appRoutes } from '@clearverify/constants';

import { Button, ClearVerifyLogo, Link } from 'src/components/common';
import { useLoginUrl } from 'src/hooks/auth';
import { useScreenInfo } from 'src/hooks/common';
import { SxProp } from 'src/types/mui';

import { AuthRoleCard } from './components';

export const AuthWelcomeScreen: React.FC = () => {
  const { isMobile } = useScreenInfo();
  const loginUrl = useLoginUrl(appRoutes.auth.login);

  const styles = isMobile ? componentStylesMobile : componentStyles;

  return (
    <Box display="flex" flexDirection="column" alignItems="center" sx={styles.container}>
      <ClearVerifyLogo sx={styles.logo} />

      <Typography variant="body2" fontSize="24px" sx={styles.welcomeText}>
        Welcome to Clear Verify
      </Typography>

      <Typography variant="body2" fontSize="16px" mt={0.5} color="GrayText" sx={styles.guideText}>
        Hello! To get started, select your account type below
      </Typography>

      <Box display="flex" pt={4} pb={2} sx={styles.menuList}>
        <AuthRoleCard
          dataElement="consumer-login-card"
          title="Employee"
          text="Available to individuals needing verification of their employment or income"
          icon={<PeopleAltIcon sx={iconSx} />}
          signInButton={
            <Button component={Link} variant="outlined" size="small" to={loginUrl}>
              Log In
            </Button>
          }
        />
        <AuthRoleCard
          dataElement="employer-login-card"
          title="Employer"
          text="Available to Clear Verify Clients"
          icon={<WorkIcon sx={iconSx} />}
          signInButton={
            <Button component={Link} variant="outlined" size="small" to={loginUrl}>
              Log In
            </Button>
          }
        />
        <AuthRoleCard
          dataElement="commercial-verifier-login-card"
          title="Commercial Verifier"
          text="Available for Commercial Verifiers needing to obtain income and employment information"
          icon={<VerifiedIcon sx={iconSx} />}
          signInButton={
            <Button component={Link} variant="outlined" size="small" to={loginUrl}>
              Log In
            </Button>
          }
          signUpButton={
            <Button
              component={Link}
              variant="outlined"
              size="small"
              to={appRoutes.auth.verifierSignup}
            >
              Sign Up
            </Button>
          }
        />
        <AuthRoleCard
          dataElement="governmental-verifier-login-card"
          title="Governmental Verifier"
          text="Available for agencies managed by local, state, or federal governments"
          icon={<FlagCirgleIcon sx={iconSx} />}
          signInButton={
            <Button component={Link} variant="outlined" size="small" to={loginUrl}>
              Log In
            </Button>
          }
          signUpButton={
            <Button
              component={Link}
              variant="outlined"
              size="small"
              to={appRoutes.auth.verifierSignup}
            >
              Sign Up
            </Button>
          }
        />
      </Box>
    </Box>
  );
};

const iconSx: SxProp = { fontSize: 32, color: 'GrayText' };

const componentStyles: Record<string, SxProp> = {
  container: {
    width: '100%',
    maxWidth: 880,
    borderRadius: '8px',
    px: 2,
    py: 2,
    margin: '0 auto',
    backgroundColor: '#FFFFFF',
    boxShadow:
      '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)',
  },

  menuList: {
    flexDirection: 'row',
  },
};

const componentStylesMobile: Record<string, SxProp> = {
  container: {
    width: '100%',
    py: 2,
    margin: '0 auto',
  },

  logo: {
    alignSelf: 'flex-start',
    fontSize: 0,
    mt: 2,
    mb: 4,
  },

  welcomeText: {
    alignSelf: 'flex-start',
  },

  guideText: {
    alignSelf: 'flex-start',
  },

  menuList: {
    flexDirection: 'column',
  },
};
